import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { selectCurrentEscritorio } from '@/features/escritorio';
import { getStoredWorkspaceId } from '@/shared/utils/workspace';
import { useStoredWorkspaceId } from '@/shared/hooks/useStoredWorkspaceId';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { cancelNegociacaoIndividualStart, clearPhoneConflict, closePhoneConflictWizard, createNegociacaoIndividual, fetchNegociacaoIndividualAgent, fetchNegociacaoIndividualAgents, validatePhoneConflictWizard, setClaimAmount, setCounterpartyField, setSelectedAgentId, setStartMode, setScheduledDate, setScheduledTime, setAgentInputValue, setTitle, setObjective, setExtrajudicialItemId, validarCpfCnpjWizard, selectWizardAgents, selectWizardAgentsPagination, selectWizardAgentsError, selectWizardAgentsLoading, selectWizardAgentDetailsLoading, selectWizardSelectedAgent, selectWizardSelectedAgentId, selectWizardCounterparty, selectWizardDocValidation, selectWizardForm, selectWizardSchedule, selectWizardSubmit, selectWizardCancelStart, selectWizardPhoneConflict, selectWizardPhoneConflictState, resetWizard } from '../store';
import { derivarParametrosDoAgente } from '../utils/derivarParametrosDoAgente';
import type { ClosePhoneConflictResult, CreateNegociacaoIndividualInput, StartMode, ValidatePhoneConflictInput, ValidatePhoneConflictResult } from '../types';
function buildScheduledFor(date: string, time: string): string | undefined {
  if (!date || !time) {
    return undefined;
  }
  const [year, month, day] = date.split('-').map(value => Number(value));
  const [hours, minutes] = time.split(':').map(value => Number(value));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return undefined;
  }
  const local = new Date(year, month - 1, day, hours, minutes, 0, 0);
  if (Number.isNaN(local.getTime())) {
    return undefined;
  }
  return local.toISOString();
}
export function useNegociacaoIndividualWizard() {
  const $ = _c(88);
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector(selectCurrentEscritorio)?.id;
  const storedWorkspaceId = useStoredWorkspaceId();
  const agents = useAppSelector(selectWizardAgents);
  const agentsPagination = useAppSelector(selectWizardAgentsPagination);
  const agentsLoading = useAppSelector(selectWizardAgentsLoading);
  const agentDetailsLoading = useAppSelector(selectWizardAgentDetailsLoading);
  const agentsError = useAppSelector(selectWizardAgentsError);
  const selectedAgentId = useAppSelector(selectWizardSelectedAgentId);
  const selectedAgent = useAppSelector(selectWizardSelectedAgent);
  const counterparty = useAppSelector(selectWizardCounterparty);
  const docValidation = useAppSelector(selectWizardDocValidation);
  const form = useAppSelector(selectWizardForm);
  const schedule = useAppSelector(selectWizardSchedule);
  const submit = useAppSelector(selectWizardSubmit);
  const cancelStart = useAppSelector(selectWizardCancelStart);
  const phoneConflict = useAppSelector(selectWizardPhoneConflict);
  const phoneConflictState = useAppSelector(selectWizardPhoneConflictState);
  let t0;
  if ($[0] !== selectedAgent) {
    t0 = derivarParametrosDoAgente(selectedAgent);
    $[0] = selectedAgent;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const agentVariables = t0;
  let t1;
  if ($[2] !== dispatch || $[3] !== storedWorkspaceId || $[4] !== workspaceId) {
    t1 = async t2 => {
      const search = t2 === undefined ? "" : t2;
      if (workspaceId && (storedWorkspaceId !== workspaceId || getStoredWorkspaceId() !== workspaceId)) {
        return;
      }
      await dispatch(fetchNegociacaoIndividualAgents({
        page: 1,
        limit: 20,
        search
      }));
    };
    $[2] = dispatch;
    $[3] = storedWorkspaceId;
    $[4] = workspaceId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const loadAgents = t1;
  let t2;
  if ($[6] !== agentsLoading || $[7] !== agentsPagination || $[8] !== dispatch) {
    t2 = async () => {
      if (agentsLoading || !agentsPagination.hasMore) {
        return;
      }
      await dispatch(fetchNegociacaoIndividualAgents({
        page: agentsPagination.page + 1,
        limit: agentsPagination.limit,
        search: agentsPagination.search
      }));
    };
    $[6] = agentsLoading;
    $[7] = agentsPagination;
    $[8] = dispatch;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const loadMoreAgents = t2;
  let t3;
  if ($[10] !== dispatch) {
    t3 = async flowId => {
      await dispatch(fetchNegociacaoIndividualAgent(flowId));
    };
    $[10] = dispatch;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const loadAgentDetails = t3;
  let t4;
  if ($[12] !== dispatch) {
    t4 = async flowId_0 => {
      dispatch(setSelectedAgentId(flowId_0));
      await dispatch(fetchNegociacaoIndividualAgent(flowId_0));
    };
    $[12] = dispatch;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const selectAgent = t4;
  let t5;
  if ($[14] !== dispatch) {
    t5 = (field, value) => {
      dispatch(setCounterpartyField({
        field,
        value
      }));
    };
    $[14] = dispatch;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const updateCounterparty = t5;
  let t6;
  if ($[16] !== dispatch) {
    t6 = async doc => {
      await dispatch(validarCpfCnpjWizard(doc));
    };
    $[16] = dispatch;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const validateCpfCnpj = t6;
  let t7;
  if ($[18] !== dispatch) {
    t7 = value_0 => {
      dispatch(setClaimAmount(value_0));
    };
    $[18] = dispatch;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const updateClaimAmount = t7;
  let t8;
  if ($[20] !== dispatch) {
    t8 = value_1 => {
      dispatch(setTitle(value_1));
    };
    $[20] = dispatch;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const updateTitle = t8;
  let t9;
  if ($[22] !== dispatch) {
    t9 = value_2 => {
      dispatch(setObjective(value_2));
    };
    $[22] = dispatch;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const updateObjective = t9;
  let t10;
  if ($[24] !== dispatch) {
    t10 = value_3 => {
      dispatch(setExtrajudicialItemId(value_3));
    };
    $[24] = dispatch;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const updateExtrajudicialItemId = t10;
  let t11;
  if ($[26] !== dispatch) {
    t11 = (key, value_4) => {
      dispatch(setAgentInputValue({
        key,
        value: value_4
      }));
    };
    $[26] = dispatch;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const updateAgentInputValue = t11;
  let t12;
  if ($[28] !== dispatch) {
    t12 = mode => {
      dispatch(setStartMode(mode));
    };
    $[28] = dispatch;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const setScheduleMode = t12;
  let t13;
  if ($[30] !== dispatch) {
    t13 = value_5 => {
      dispatch(setScheduledDate(value_5));
    };
    $[30] = dispatch;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const setScheduleDateValue = t13;
  let t14;
  if ($[32] !== dispatch) {
    t14 = value_6 => {
      dispatch(setScheduledTime(value_6));
    };
    $[32] = dispatch;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const setScheduleTimeValue = t14;
  let t15;
  if ($[34] !== counterparty || $[35] !== dispatch || $[36] !== form || $[37] !== schedule || $[38] !== selectedAgentId) {
    t15 = async () => {
      if (!selectedAgentId) {
        return;
      }
      const payload = {
        title: form.title || undefined,
        objective: form.objective || undefined,
        flowId: selectedAgentId,
        claimAmount: form.claimAmount || undefined,
        floorAmount: form.floorAmount ?? undefined,
        marginPercent: form.marginPercent ?? undefined,
        counterparty: {
          cpfCnpj: counterparty.cpfCnpj,
          name: counterparty.name,
          phone: counterparty.phone
        },
        agentInput: form.agentInput,
        startMode: schedule.startMode,
        scheduledFor: schedule.startMode === "SCHEDULED" ? buildScheduledFor(schedule.scheduledDate, schedule.scheduledTime) : undefined,
        extrajudicialItemId: form.extrajudicialItemId ?? undefined
      };
      await dispatch(createNegociacaoIndividual(payload));
    };
    $[34] = counterparty;
    $[35] = dispatch;
    $[36] = form;
    $[37] = schedule;
    $[38] = selectedAgentId;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const submitWizard = t15;
  let t16;
  if ($[40] !== dispatch || $[41] !== submit.created.caseId) {
    t16 = async () => {
      const caseId = submit.created.caseId;
      if (!caseId) {
        return;
      }
      await dispatch(cancelNegociacaoIndividualStart(caseId));
    };
    $[40] = dispatch;
    $[41] = submit.created.caseId;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const cancelStartForCreated = t16;
  let t17;
  if ($[43] !== dispatch) {
    t17 = async input => {
      const action = await dispatch(validatePhoneConflictWizard(input));
      if (validatePhoneConflictWizard.fulfilled.match(action)) {
        return action.payload;
      }
      throw new Error(action.payload || "Erro ao validar conflito por WhatsApp");
    };
    $[43] = dispatch;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  const validatePhoneConflict = t17;
  let t18;
  if ($[45] !== dispatch) {
    t18 = async caseId_0 => {
      const action_0 = await dispatch(closePhoneConflictWizard(caseId_0));
      if (closePhoneConflictWizard.fulfilled.match(action_0)) {
        return action_0.payload;
      }
      throw new Error(action_0.payload || "Erro ao encerrar negocia\xE7\xE3o em conflito");
    };
    $[45] = dispatch;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const closePhoneConflict = t18;
  let t19;
  if ($[47] !== dispatch) {
    t19 = () => {
      dispatch(clearPhoneConflict());
    };
    $[47] = dispatch;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const clearPhoneConflictData = t19;
  let t20;
  if ($[49] !== dispatch) {
    t20 = () => {
      dispatch(resetWizard());
    };
    $[49] = dispatch;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  const reset = t20;
  let t21;
  if ($[51] !== agentDetailsLoading || $[52] !== agentVariables || $[53] !== agents || $[54] !== agentsError || $[55] !== agentsLoading || $[56] !== agentsPagination || $[57] !== cancelStart || $[58] !== cancelStartForCreated || $[59] !== clearPhoneConflictData || $[60] !== closePhoneConflict || $[61] !== counterparty || $[62] !== docValidation || $[63] !== form || $[64] !== loadAgentDetails || $[65] !== loadAgents || $[66] !== loadMoreAgents || $[67] !== phoneConflict || $[68] !== phoneConflictState || $[69] !== reset || $[70] !== schedule || $[71] !== selectAgent || $[72] !== selectedAgent || $[73] !== selectedAgentId || $[74] !== setScheduleDateValue || $[75] !== setScheduleMode || $[76] !== setScheduleTimeValue || $[77] !== submit || $[78] !== submitWizard || $[79] !== updateAgentInputValue || $[80] !== updateClaimAmount || $[81] !== updateCounterparty || $[82] !== updateExtrajudicialItemId || $[83] !== updateObjective || $[84] !== updateTitle || $[85] !== validateCpfCnpj || $[86] !== validatePhoneConflict) {
    t21 = {
      agents,
      agentsLoading,
      agentsPagination,
      loadMoreAgents,
      agentDetailsLoading,
      agentsError,
      selectedAgentId,
      selectedAgent,
      agentVariables,
      counterparty,
      docValidation,
      form,
      schedule,
      submit,
      cancelStart,
      phoneConflict,
      phoneConflictState,
      loadAgents,
      loadAgentDetails,
      selectAgent,
      updateCounterparty,
      validateCpfCnpj,
      updateClaimAmount,
      updateTitle,
      updateObjective,
      updateExtrajudicialItemId,
      updateAgentInputValue,
      setScheduleMode,
      setScheduleDateValue,
      setScheduleTimeValue,
      submitWizard,
      cancelStartForCreated,
      validatePhoneConflict,
      closePhoneConflict,
      clearPhoneConflictData,
      reset
    };
    $[51] = agentDetailsLoading;
    $[52] = agentVariables;
    $[53] = agents;
    $[54] = agentsError;
    $[55] = agentsLoading;
    $[56] = agentsPagination;
    $[57] = cancelStart;
    $[58] = cancelStartForCreated;
    $[59] = clearPhoneConflictData;
    $[60] = closePhoneConflict;
    $[61] = counterparty;
    $[62] = docValidation;
    $[63] = form;
    $[64] = loadAgentDetails;
    $[65] = loadAgents;
    $[66] = loadMoreAgents;
    $[67] = phoneConflict;
    $[68] = phoneConflictState;
    $[69] = reset;
    $[70] = schedule;
    $[71] = selectAgent;
    $[72] = selectedAgent;
    $[73] = selectedAgentId;
    $[74] = setScheduleDateValue;
    $[75] = setScheduleMode;
    $[76] = setScheduleTimeValue;
    $[77] = submit;
    $[78] = submitWizard;
    $[79] = updateAgentInputValue;
    $[80] = updateClaimAmount;
    $[81] = updateCounterparty;
    $[82] = updateExtrajudicialItemId;
    $[83] = updateObjective;
    $[84] = updateTitle;
    $[85] = validateCpfCnpj;
    $[86] = validatePhoneConflict;
    $[87] = t21;
  } else {
    t21 = $[87];
  }
  return t21;
}